import { FC, PropsWithChildren } from 'react'

import { match } from 'ts-pattern'
import { GreyGrey20, Flex } from '@sequencehq/core-components'
import { useCubeContext } from 'Cube/communication/internal/cube.domain.context'
import {
  ButtonCopyPublishedLink,
  ButtonDownloadQuotePdf,
  ButtonViewSchedule
} from '../../buttons'
import { PublishedStatus, usePublishedStatus } from '../../hooks'
import { SidebarSection } from '../SidebarSection'
import { StatusBadge } from '../StatusBadge'
import { SignatureProgress } from '../SignatureProgress'

/**
 * Styles to match buttons to how <StatusBadge /> looks
 */
const buttonStyle = {
  backgroundColor: GreyGrey20,
  border: 'none',
  boxShadow: 'none'
}

export const PublishedStatusV2: FC = () => {
  const ctx = useCubeContext()
  const publishedStatus = usePublishedStatus()

  const isSignaturesEnabled = ctx.queries.rawData.data.quote.isSignaturesEnabled
  const isArchived = ctx.queries.rawData.data.common.isArchived

  /**
   * WaitingForApproval always renders <SignatureProgress /> regardless of signature settings.
   */
  if (publishedStatus === PublishedStatus.WaitingForApproval) {
    return <SignatureProgress />
  }

  /**
   * If signatures are not enabled for a quote, or the quote has a status where signature activity is irrelevant,
   * fallback to the simple <StatusBadge /> component.
   */
  if (
    !isSignaturesEnabled ||
    isArchived ||
    publishedStatus === PublishedStatus.Template ||
    publishedStatus === PublishedStatus.NotPublished
  ) {
    return (
      <CollapsibleSection>
        <Flex
          gap="8px"
          width="100%"
          data-testid="quote.publishedStatus.fallback"
        >
          <StatusBadge status={publishedStatus} data-testid="quote.status" />
          {match(publishedStatus)
            .with(PublishedStatus.Published, PublishedStatus.Accepted, () => (
              <>
                <ButtonCopyPublishedLink
                  style={buttonStyle}
                  data-testid="quote.publishedStatus.fallback.copyPublishedLink"
                />
                <ButtonDownloadQuotePdf
                  style={buttonStyle}
                  data-testid="quote.publishedStatus.fallback.downloadPdf"
                />
              </>
            ))
            .with(PublishedStatus.Executed, () => (
              <>
                <ButtonCopyPublishedLink
                  style={buttonStyle}
                  data-testid="quote.publishedStatus.fallback.copyPublishedLink"
                />
                <ButtonDownloadQuotePdf
                  style={buttonStyle}
                  data-testid="quote.publishedStatus.fallback.downloadPdf"
                />
                <ButtonViewSchedule
                  variant="icon"
                  style={buttonStyle}
                  data-testid="quote.publishedStatus.fallback.viewSchedule"
                />
              </>
            ))
            .with(
              PublishedStatus.NotPublished,
              PublishedStatus.Template,
              PublishedStatus.Archived,
              // not relevant when eSignatures is disabled
              PublishedStatus.PreparingSignatureRequest,
              PublishedStatus.ReadyToSign,
              PublishedStatus.ProcessingSignatures,
              () => null
            )
            .exhaustive()}
        </Flex>
      </CollapsibleSection>
    )
  }

  return <SignatureProgress />
}

const CollapsibleSection: FC<PropsWithChildren> = ({ children }) => (
  <Flex width="100%" padding="8px 8px 0 8px">
    <SidebarSection
      title="Status"
      showBorder
      data-testid="quote.sidebarSection.publishedStatus"
    >
      <Flex marginBottom="16px">{children}</Flex>
    </SidebarSection>
  </Flex>
)
